// Compatibility adapter for mixpanel-browser 2.80.0. Its public start promise
// doesn't await resumeRecording's IndexedDB lookup. Guard the actual recorder
// methods, including recordings restored automatically during SDK init.
export const installMixpanelReplayGuard = (sdk, getPredicate) => {
    const manager = sdk.recorderManager;
    const descriptor = manager && Object.getOwnPropertyDescriptor(manager, '_recorder');
    if (!descriptor?.configurable || !('value' in descriptor)) {
        throw new Error('Unsupported Mixpanel recorder manager');
    }

    let recorder = descriptor.value;
    const guarded = new WeakSet();
    const protect = value => {
        if (!value || guarded.has(value)) return value;
        if (typeof value.startRecording !== 'function' || typeof value.resumeRecording !== 'function') {
            throw new Error('Unsupported Mixpanel recorder');
        }
        const start = value.startRecording;
        const resume = value.resumeRecording;
        let resumes = Promise.resolve();
        let resumingPredicate = null;

        value.startRecording = function (...args) {
            const isCurrent = resumingPredicate || getPredicate();
            if (!getPredicate()() || !isCurrent()) return Promise.resolve(null);
            return start.apply(this, args);
        };
        value.resumeRecording = function (...args) {
            const isCurrent = getPredicate();
            const target = this;
            const operation = resumes.then(async () => {
                if (!isCurrent()) return null;
                resumingPredicate = isCurrent;
                try {
                    return await resume.apply(target, args);
                } finally {
                    resumingPredicate = null;
                }
            });
            // A failed resume must not block future eligible sessions.
            resumes = operation.catch(() => null);
            return operation;
        };
        guarded.add(value);
        return value;
    };

    recorder = protect(recorder);
    Object.defineProperty(manager, '_recorder', {
        configurable: false,
        enumerable: descriptor.enumerable,
        get: () => recorder,
        set: value => { recorder = protect(value); }
    });
};
